import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { newsletterTerms, currentSkin } from "@/lib/skin";
import { StatusBadge } from "@/components/StatusBadge";
import { TestBadge } from "@/components/TestBadge";
import { Card } from "@/components/ui/card";
import {
  PlusCircle, FileText, ListChecks, AlertCircle, CalendarDays, BookOpen,
  Palette, ArrowRight, FileEdit, CheckCircle2, Clock,
} from "lucide-react";
import { format } from "date-fns";
import { ASSETS } from "@/lib/assets";

const ACTIONS = [
  { to: "/submit/sunday-news", label: "Submit Sunday News Item", desc: "Announcements ready for review & publication", icon: PlusCircle, accent: true, feature: "publication_builder" },
  { to: "/submit/comms-request", label: "Submit Communications Request", desc: "Flyers, graphics, campaigns & creative support", icon: FileText },
  { to: "/submissions", label: "View My Submissions", desc: "Track status of everything you've submitted", icon: ListChecks },
  { to: "/submissions?filter=revisions", label: "Complete Requested Information", desc: "Respond to Communications' requests for more info", icon: FileEdit },
  { to: "/calendar", label: "Communications Calendar", desc: "See scheduled publications & events", icon: CalendarDays, feature: "calendar" },
  { to: "/guidelines", label: "Guidelines & Brand Resources", desc: "Submission rules, templates & assets", icon: BookOpen },
];

const stat = (label, value, icon, testId) => ({ label, value, icon, testId });

export default function MemberDashboard() {
  const { user, features } = useAuth();
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  const terms = newsletterTerms();
  const actions = ACTIONS.filter((a) => !a.feature || features[a.feature])
    .map((a) => (a.to === "/submit/sunday-news" ? { ...a, label: `${terms.submit} Item` } : a));

  useEffect(() => {
    api.get("/dashboard/member").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const stats = data ? [
    stat("In Progress", data.in_progress, Clock, "stat-in-progress"),
    stat("Needs Revision", data.needs_revision, AlertCircle, "stat-needs-revision"),
    stat("Published", data.published, CheckCircle2, "stat-published"),
    stat("Drafts", data.drafts, FileEdit, "stat-drafts"),
  ] : [];

  return (
    <div className="mx-auto max-w-7xl space-y-8">
      {/* Hero */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-2xl bg-primary p-8 text-primary-foreground lg:p-10">
        <img src={ASSETS.hero} alt="Chapter members collaborating"
          className="absolute right-0 top-0 h-full w-1/2 object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/95 to-primary/50" />
        <img src={ASSETS.goldLines} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="relative z-10 max-w-xl">
          <p className="text-xs uppercase tracking-[0.18em] leading-relaxed text-primary-foreground/80">
            Alpha Kappa Alpha Sorority, Incorporated®<br />Alpha Epsilon Gamma Omega Chapter
          </p>
          <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight sm:text-4xl">
            {currentSkin() === "aka"
              ? `Welcome back Soror ${(user?.name || "").replace(/^soror\s+/i, "")}`
              : `Welcome back, ${user?.name?.split(" ")[0]}.`}
          </h1>
          <p className="mt-2 text-primary-foreground/80">Submit an announcement or request, and track it from draft to publication.</p>
          <div className="mt-5 flex flex-wrap gap-3">
            {features.publication_builder && (
              <button onClick={() => navigate("/submit/sunday-news")} data-testid="hero-sunday-news-btn"
                style={{ backgroundColor: "hsl(345 48% 70%)" }}
                className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5">
                <PlusCircle className="h-4 w-4" /> {terms.submit}
              </button>
            )}
            <button onClick={() => navigate("/submit/comms-request")} data-testid="hero-comms-btn"
              className="inline-flex items-center gap-2 rounded-full bg-primary-foreground/15 px-5 py-2.5 text-sm font-semibold transition-colors hover:bg-primary-foreground/25">
              <Palette className="h-4 w-4" /> Communications Request
            </button>
          </div>
        </div>
      </motion.div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map((s, i) => (
          <motion.div key={s.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
            <Card className="flex items-center gap-4 border-border p-5 transition-transform hover:-translate-y-1 hover:shadow-md" data-testid={s.testId}>
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <s.icon className="h-5 w-5" />
              </div>
              <div>
                <p className="font-heading text-2xl font-bold">{s.value}</p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Actions */}
      <div>
        <h2 className="font-heading text-xl font-semibold tracking-tight">What would you like to do?</h2>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {actions.map((a, i) => (
            <motion.div key={a.to} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + i * 0.04 }}>
              <Link to={a.to} data-testid={`action-${a.to.split("/").pop().split("?")[0]}`}
                style={a.accent ? { backgroundColor: "hsl(345 48% 97%)", borderColor: "hsl(345 48% 82%)" } : undefined}
                className={`group flex h-full flex-col rounded-xl border p-5 transition-transform hover:-translate-y-1 hover:shadow-md ${a.accent ? "" : "border-border bg-card"}`}>
                <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${a.accent ? "text-white" : "bg-primary/10 text-primary"}`}
                  style={a.accent ? { backgroundColor: "hsl(345 48% 70%)" } : undefined}>
                  <a.icon className="h-5 w-5" />
                </div>
                <p className="mt-4 font-heading font-semibold">{a.label}</p>
                <p className="mt-1 flex-1 text-sm text-muted-foreground">{a.desc}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary">
                  Open <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Recent */}
      <div>
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-xl font-semibold tracking-tight">Recent submissions</h2>
          <Link to="/submissions" className="text-sm font-medium text-primary hover:underline" data-testid="view-all-submissions">View all</Link>
        </div>
        <Card className="mt-4 divide-y divide-border p-0">
          {(!data || data.recent.length === 0) && (
            <p className="p-8 text-center text-sm text-muted-foreground">No submissions yet. Start with a request above.</p>
          )}
          {data?.recent.map((s) => (
            <Link key={s.id} to={`/submissions/${s.id}`} data-testid={`recent-item-${s.id}`}
              className="flex items-center justify-between gap-4 p-4 transition-colors hover:bg-secondary/50">
              <div className="min-w-0">
                <p className="truncate font-medium">{s.data?.title || s.data?.project_purpose || "Untitled"}</p>
                <p className="text-xs text-muted-foreground">
                  {s.submission_type === "sunday_news" ? "Sunday News" : "Communications Request"} · {s.created_at ? format(new Date(s.created_at), "MMM d, yyyy") : ""}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {s.is_test && <TestBadge />}
                <StatusBadge status={s.status} />
              </div>
            </Link>
          ))}
        </Card>
      </div>
    </div>
  );
}
